// import axios from "axios"; // [Google API - kept for reference]
import { callAPI } from "../helper/apiUtils";

// ---------------------------------------------------------------------------
// Skip translation for emoji-only / trivial inputs (no translatable words)
// ---------------------------------------------------------------------------
/**
 * True when the text has no translatable words (emoji-only, punctuation,
 * bare URLs/numbers). Mirrors the backend TranslationService skip so the
 * client never shows "Translating..." for these messages.
 */
export function isUntranslatableText(text) {
  if (!text || typeof text !== 'string') return true;
  const trimmed = text.trim();
  if (!trimmed) return true;
  // Ciphertext must be decrypted by the caller before this check.
  if (trimmed.startsWith('U2FsdGVkX1')) return false;
  const stripped = trimmed
    .replace(/https?:\/\/\S+/gi, '')
    .replace(/\b[\w.+-]+@[\w-]+\.[\w.-]+\b/gi, '')
    .replace(/@\[[^\]]+\]\([^)]+\)/g, '')
    .replace(/<[^>]+>/g, '')
    .replace(/[\d\s\p{P}\p{S}\p{Extended_Pictographic}\p{Emoji_Modifier}\u200D\uFE0F\uFE0E]/gu, '');
  return stripped.length === 0;
}

// ---------------------------------------------------------------------------
// Content comparison – strips formatting/markup to detect same-language content
// ---------------------------------------------------------------------------
function contentMatchesOriginal(translated, original) {
  if (!translated || !original) return false;
  if (translated === original) return true;
  if (translated.trim() === original.trim()) return true;
  const strip = (s) => s
    .replace(/<[^>]+>/g, '')
    .replace(/&\w+;/g, '')
    .replace(/[*~_`#\\\-[\](){}|!+]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return strip(translated) === strip(original);
}

// ---------------------------------------------------------------------------
// Translation Cache (LRU, in-memory)
// ---------------------------------------------------------------------------
const translationCache = new Map();
const MAX_CACHE_SIZE = 3000;

// In-flight request deduplication: prevents duplicate API calls when
// multiple callers request the same translation before the first resolves.
const inflightRequests = new Map();

function getCacheKey(text, lang) {
  return lang + '\0' + text;
}

function markdownInflightKey(text, lang) {
  return 'md\0' + getCacheKey(text, lang);
}

function setInflightKeys(text, lang, isMarkdown, promise) {
  const base = getCacheKey(text, lang);
  inflightRequests.set(base, promise);
  if (isMarkdown) inflightRequests.set(markdownInflightKey(text, lang), promise);
}

function deleteInflightKeys(text, lang, isMarkdown) {
  const base = getCacheKey(text, lang);
  inflightRequests.delete(base);
  if (isMarkdown) inflightRequests.delete(markdownInflightKey(text, lang));
}

function getInflightTranslation(text, lang, isMarkdown) {
  const base = getCacheKey(text, lang);
  if (isMarkdown) {
    return inflightRequests.get(markdownInflightKey(text, lang)) || inflightRequests.get(base);
  }
  return inflightRequests.get(base) || inflightRequests.get(markdownInflightKey(text, lang));
}

function getCachedTranslation(text, lang) {
  if (!text) return undefined;
  const key = getCacheKey(text, lang);
  const cached = translationCache.get(key);
  if (cached !== undefined) {
    translationCache.delete(key);
    translationCache.set(key, cached);
  }
  return cached;
}

function setCachedTranslation(text, lang, translation) {
  if (!text || !translation) return;
  const key = getCacheKey(text, lang);
  if (translationCache.has(key)) {
    translationCache.delete(key);
  } else if (translationCache.size >= MAX_CACHE_SIZE) {
    const firstKey = translationCache.keys().next().value;
    translationCache.delete(firstKey);
  }
  translationCache.set(key, translation);
}

export function clearTranslationCache() {
  translationCache.clear();
}

// ---------------------------------------------------------------------------
// Concurrency Limiter – at most CONCURRENCY_LIMIT parallel HTTP requests
// ---------------------------------------------------------------------------
const CONCURRENCY_LIMIT = 5;
let activeRequests = 0;
const requestQueue = [];

function throttledRequest(fn) {
  return new Promise((resolve, reject) => {
    const execute = async () => {
      activeRequests++;
      try {
        resolve(await fn());
      } catch (e) {
        reject(e);
      } finally {
        activeRequests--;
        if (requestQueue.length > 0) {
          requestQueue.shift()();
        }
      }
    };
    if (activeRequests < CONCURRENCY_LIMIT) {
      execute();
    } else {
      requestQueue.push(execute);
    }
  });
}

// ---------------------------------------------------------------------------
// Markdown ↔ HTML helpers (unchanged)
// ---------------------------------------------------------------------------

export const markdownToHtmlForTranslation = (text) => {
  if (!text) return text;
  let html = text;
  html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  html = html.replace(/\*\*([\s\S]*?)\*\*/g, '<b>$1</b>');
  html = html.replace(/~~([\s\S]*?)~~/g, '<del>$1</del>');
  html = html.replace(/@\[([^\]]*)\]\(([^)]*)\)/g,
    '<span class="notranslate" data-mention-id="$2">@$1</span>');
  html = html.replace(/\[([^\]]*)\]\(([^)]*)\)/g, '<a href="$2">$1</a>');
  html = html.replace(/\n/g, '<br>');
  return html;
};

export const decodeHtmlEntities = (text) => {
  if (!text) return text;
  let result = text;
  result = result.replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  result = result.replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(parseInt(dec, 10)));
  result = result.replace(/&nbsp;/g, ' ');
  result = result.replace(/&quot;/g, '"');
  result = result.replace(/&apos;/g, "'");
  result = result.replace(/&lt;/g, '<');
  result = result.replace(/&gt;/g, '>');
  result = result.replace(/&amp;/g, '&');
  return result;
};

// ---------------------------------------------------------------------------
// Formatting preservation – the translation backend (LLM) sometimes adds
// emphasis (<b>/<strong>/etc.) around words that were NOT formatted in the
// original message. That changes the font style on translation, which we don't
// want. This strips bold/strikethrough markers introduced by the translation
// when the original had none, while keeping formatting that genuinely existed.
// ---------------------------------------------------------------------------
// Bold: **x**, __x__, <b>/<strong>
const hasBold = (s) =>
  /\*\*[\s\S]*?\*\*/.test(s || '') ||
  /__[\s\S]*?__/.test(s || '') ||
  /<(b|strong)\b[^>]*>/i.test(s || '');
// Strikethrough: ~~x~~, <del>/<s>/<strike>
const hasStrike = (s) =>
  /~~[\s\S]*?~~/.test(s || '') ||
  /<(del|s|strike)\b[^>]*>/i.test(s || '');
// Italic: *x* / _x_ (word-bounded, not the bold **/__ markers), <i>/<em>
const hasItalic = (s) =>
  /(^|[^*])\*(?!\*)[^*\n]+?\*(?!\*)/.test(s || '') ||
  /(^|[^\w_])_(?!_)[^_\n]+?_(?!_)($|[^\w_])/.test(s || '') ||
  /<(i|em)\b[^>]*>/i.test(s || '');
// Underline: <u>
const hasUnderline = (s) => /<u\b[^>]*>/i.test(s || '');

// Remove emphasis/formatting markers that the translation introduced but the
// original message did NOT have, so translating a message never changes its
// font style (no unexpected bold/italic/underline/strikethrough). Formatting
// that genuinely existed in the original is preserved. Mentions and links are
// left untouched (they carry meaning, not styling).
export const preserveOriginalFormatting = (translated, original) => {
  if (!translated) return translated;
  let result = translated;
  // Bold first so the ** / __ markers are gone before italic stripping runs.
  if (!hasBold(original)) {
    result = result.replace(/\*\*([\s\S]*?)\*\*/g, '$1');
    result = result.replace(/__([\s\S]*?)__/g, '$1');
    result = result.replace(/<(b|strong)\b[^>]*>([\s\S]*?)<\/\1>/gi, '$2');
  }
  if (!hasStrike(original)) {
    result = result.replace(/~~([\s\S]*?)~~/g, '$1');
    result = result.replace(/<(del|s|strike)\b[^>]*>([\s\S]*?)<\/\1>/gi, '$2');
  }
  if (!hasItalic(original)) {
    // Single-asterisk italic (never touches ** which is already handled above).
    result = result.replace(/(^|[^*])\*(?!\*)([^*\n]+?)\*(?!\*)/g, '$1$2');
    // Word-bounded single-underscore italic (mirror GFM: intraword _ like
    // file_name or a_b in URLs is literal, so leave those alone).
    result = result.replace(/(^|[^\w_])_(?!_)([^_\n]+?)_(?!_)(?=$|[^\w_])/g, '$1$2');
    result = result.replace(/<(i|em)\b[^>]*>([\s\S]*?)<\/\1>/gi, '$2');
  }
  if (!hasUnderline(original)) {
    result = result.replace(/<u\b[^>]*>([\s\S]*?)<\/u>/gi, '$1');
  }
  return result;
};

export const htmlToMarkdownFromTranslation = (html) => {
  if (!html) return '';
  let md = html;
  md = md.replace(/<b>([\s\S]*?)<\/b>/gi, '**$1**');
  md = md.replace(/<strong>([\s\S]*?)<\/strong>/gi, '**$1**');
  md = md.replace(/<del>([\s\S]*?)<\/del>/gi, '~~$1~~');
  md = md.replace(/<s>([\s\S]*?)<\/s>/gi, '~~$1~~');
  md = md.replace(/<strike>([\s\S]*?)<\/strike>/gi, '~~$1~~');
  md = md.replace(/<span[^>]*class="notranslate"[^>]*data-mention-id="([^"]*)"[^>]*>([\s\S]*?)<\/span>/gi,
    (_, id, name) => `@[${name.replace(/^@/, '')}](${id})`);
  md = md.replace(/<a\s+href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, '[$2]($1)');
  md = md.replace(/<br\s*\/?>/gi, '\n');
  md = md.replace(/<[^>]+>/g, '');
  md = decodeHtmlEntities(md);
  return md;
};

// ---------------------------------------------------------------------------
// Single-message translation (cached + throttled)
// ---------------------------------------------------------------------------

/**
 * Translate plain text via the backend /message/translate endpoint.
 * Returns the translated string, or the original text on failure.
 */
export const getTranslateText = async (text, lang) => {
  if (!text) return text;
  if (isUntranslatableText(text)) return text;
  const cached = getCachedTranslation(text, lang);
  // Identity results (already in the target language) are cached on purpose so
  // we do not re-hit /message/translate on every chat open.
  if (cached !== undefined) return cached;

  const inflight = getInflightTranslation(text, lang, false);
  if (inflight) return inflight;

  const promise = (async () => {
    try {
      return await throttledRequest(async () => {
        const rechecked = getCachedTranslation(text, lang);
        if (rechecked !== undefined) return rechecked;
        const { data } = await callAPI(
          '/message/translate', {}, 'POST',
          { message: text, language: lang }
        );
        const translated = data?.translatedMessage || text;
        const result = contentMatchesOriginal(translated, text) ? text : translated;
        setCachedTranslation(text, lang, result);
        return result;
      });
    } catch (e) {
      return text;
    } finally {
      deleteInflightKeys(text, lang, false);
    }
  })();

  setInflightKeys(text, lang, false, promise);
  return promise;
};

// /** [Google API - kept for reference]
//  * Translate plain text via the Google Translate API.
//  * Returns the translated string, or the original text on failure.
//  */
// export const getTranslateText = async (text, lang) => {
//   if (!text) return text;
//   try {
//     const {
//       data: { data },
//     } = await axios.get(
//       `https://translation.googleapis.com/language/translate/v2`,
//       { params: { target: lang, key: process.env.REACT_APP_GOOGLE_TRANSLATE_KEY, q: text } }
//     );
//     const translated = data?.translations[0]?.translatedText || text;
//     return decodeHtmlEntities(translated);
//   } catch (e) {
//     return text;
//   }
// };

/**
 * Translate a markdown message via the backend /message/translate endpoint
 * (preserving markdown formatting and mentions through HTML round-tripping).
 * Returns the translated markdown string, or the original on failure.
 */
export const translateMessageContent = async (message, lang) => {
  if (!message) return message;
  if (isUntranslatableText(message)) return message;
  const cached = getCachedTranslation(message, lang);
  if (cached !== undefined) return cached;

  const inflight = getInflightTranslation(message, lang, true);
  if (inflight) return inflight;

  const promise = (async () => {
    try {
      return await throttledRequest(async () => {
        const rechecked = getCachedTranslation(message, lang);
        if (rechecked !== undefined) return rechecked;
        const htmlMessage = markdownToHtmlForTranslation(message);
        const { data } = await callAPI(
          '/message/translate', {}, 'POST',
          { message: htmlMessage, language: lang }
        );
        const translatedHtml = data?.translatedMessage;
        if (!translatedHtml || contentMatchesOriginal(translatedHtml, htmlMessage)) {
          setCachedTranslation(message, lang, message);
          return message;
        }
        const translatedMd = htmlToMarkdownFromTranslation(translatedHtml) || message;
        const translated = preserveOriginalFormatting(translatedMd, message);
        const result = contentMatchesOriginal(translated, message) ? message : translated;
        setCachedTranslation(message, lang, result);
        return result;
      });
    } catch (e) {
      return message;
    } finally {
      deleteInflightKeys(message, lang, true);
    }
  })();

  setInflightKeys(message, lang, true, promise);
  return promise;
};

// /** [Google API - kept for reference]
//  * Translate a markdown message via the Google Translate API (preserving
//  * markdown formatting and mentions through HTML round-tripping).
//  * Returns the translated markdown string, or the original on failure.
//  */
// export const translateMessageContent = async (message, lang) => {
//   if (!message) return message;
//   try {
//     const htmlMessage = markdownToHtmlForTranslation(message);
//     const {
//       data: { data },
//     } = await axios.get(
//       `https://translation.googleapis.com/language/translate/v2`,
//       { params: { target: lang, key: process.env.REACT_APP_GOOGLE_TRANSLATE_KEY, format: 'html', q: htmlMessage } }
//     );
//     const translatedHtml = data?.translations[0]?.translatedText;
//     return htmlToMarkdownFromTranslation(translatedHtml) || message;
//   } catch (e) {
//     return message;
//   }
// };

// ---------------------------------------------------------------------------
// Batch Translation API (tries /message/translate-batch, falls back to
// individual throttled+cached calls if endpoint unavailable)
// ---------------------------------------------------------------------------
let batchEndpointAvailable = true;

function processBatchTranslation(orig, translated) {
  if (!orig) return translated;
  if (orig.isMarkdown) {
    const htmlOrig = markdownToHtmlForTranslation(orig.message);
    if (contentMatchesOriginal(translated, htmlOrig)) {
      return orig.message;
    }
    let md = htmlToMarkdownFromTranslation(translated) || orig.message;
    md = preserveOriginalFormatting(md, orig.message);
    if (contentMatchesOriginal(md, orig.message)) return orig.message;
    return md;
  }
  if (contentMatchesOriginal(translated, orig.message)) return orig.message;
  return translated;
}

/**
 * Translate multiple messages in one round-trip.
 * Each item: { id: string, message: string, isMarkdown?: boolean }
 * Returns: [{ id, translatedMessage }]
 */
export const translateMessagesBatch = async (messages, lang) => {
  if (!messages || messages.length === 0) return [];

  const results = new Map();
  const needFetch = [];
  const waiting = [];

  for (const item of messages) {
    if (!item?.message) {
      results.set(item.id, item.message);
      continue;
    }
    if (isUntranslatableText(item.message)) {
      results.set(item.id, item.message);
      continue;
    }
    const cached = getCachedTranslation(item.message, lang);
    if (cached !== undefined) {
      results.set(item.id, cached);
      continue;
    }
    const inflight = getInflightTranslation(item.message, lang, !!item.isMarkdown);
    if (inflight) {
      waiting.push({ item, inflight });
    } else {
      needFetch.push(item);
    }
  }

  if (waiting.length > 0) {
    await Promise.all(waiting.map(async ({ item, inflight }) => {
      try {
        results.set(item.id, await inflight);
      } catch (_) {
        results.set(item.id, item.message);
      }
    }));
  }

  if (needFetch.length === 0) {
    return messages.map(m => ({
      id: m.id,
      translatedMessage: results.get(m.id) ?? m.message,
    }));
  }

  const applyFetched = (id, orig, translated) => {
    const value = processBatchTranslation(orig, translated);
    if (orig) setCachedTranslation(orig.message, lang, value);
    results.set(id, value);
    return value;
  };

  if (batchEndpointAvailable && needFetch.length > 1) {
    // Register inflight before the HTTP call so concurrent getTranslate /
    // translateMessageContent callers wait on this batch instead of firing
    // their own /message/translate requests.
    let batchResolve;
    const fetchedMap = new Map();
    const batchPromise = new Promise((resolve) => { batchResolve = resolve; });
    for (const item of needFetch) {
      const itemPromise = batchPromise.then((map) => map.get(item.id) ?? item.message);
      setInflightKeys(item.message, lang, !!item.isMarkdown, itemPromise);
    }

    try {
      const payload = needFetch.map(m => ({
        id: m.id,
        message: m.isMarkdown ? markdownToHtmlForTranslation(m.message) : m.message,
      }));

      const { data } = await callAPI('/message/translate-batch', {}, 'POST', {
        messages: payload,
        language: lang,
      });

      if (data?.translations) {
        for (const t of data.translations) {
          const orig = needFetch.find(m => String(m.id) === String(t.id));
          const value = applyFetched(orig?.id ?? t.id, orig, t.translatedMessage);
          fetchedMap.set(orig?.id ?? t.id, value);
        }
        for (const item of needFetch) {
          if (!fetchedMap.has(item.id)) {
            applyFetched(item.id, item, item.message);
            fetchedMap.set(item.id, item.message);
          }
        }
        batchResolve(fetchedMap);
        return messages.map(m => ({
          id: m.id,
          translatedMessage: results.get(m.id) ?? m.message,
        }));
      }
    } catch (e) {
      if (e?.response?.status === 404 || e?.response?.status === 405) {
        batchEndpointAvailable = false;
      }
    } finally {
      for (const item of needFetch) {
        deleteInflightKeys(item.message, lang, !!item.isMarkdown);
      }
      if (fetchedMap.size === 0) batchResolve(fetchedMap);
    }
  }

  await Promise.all(
    needFetch.map(async (item) => {
      if (results.has(item.id)) return;
      const translated = item.isMarkdown
        ? await translateMessageContent(item.message, lang)
        : await getTranslateText(item.message, lang);
      results.set(item.id, translated);
    })
  );

  return messages.map(m => ({
    id: m.id,
    translatedMessage: results.get(m.id) || m.message,
  }));
};

// ---------------------------------------------------------------------------
// Background pre-translation – warms the cache for all chats so that
// opening a conversation shows translations instantly.
// ---------------------------------------------------------------------------
let preTranslateGeneration = 0;

// Chat ids whose pre-translation /getChat call returned status:false
// ("group does not exist"). Persisted to sessionStorage so we don't keep
// re-issuing those failing requests on every navigation/refresh.
const FAILED_PRE_TRANSLATE_CHATS_KEY = 'kjuup.preTranslate.failedChatIds';

function loadFailedPreTranslateChatIds() {
  try {
    if (typeof sessionStorage === 'undefined') return new Set();
    const raw = sessionStorage.getItem(FAILED_PRE_TRANSLATE_CHATS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch (_) {
    return new Set();
  }
}

function persistFailedPreTranslateChatIds(set) {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(FAILED_PRE_TRANSLATE_CHATS_KEY, JSON.stringify([...set]));
  } catch (_) {
    // ignore quota / private mode errors
  }
}

const failedPreTranslateChatIds = loadFailedPreTranslateChatIds();

export function clearFailedPreTranslateChatIds() {
  failedPreTranslateChatIds.clear();
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(FAILED_PRE_TRANSLATE_CHATS_KEY);
    }
  } catch (_) { /* noop */ }
}

/**
 * Pre-translate the first page of messages for each chat in the list.
 * Requests server-side translations via translationLang param. Only
 * falls back to client-side LLM translation for messages that don't
 * have a persisted translation yet.
 *
 * @param {Array} chats – chat list items (shape from getUserChats / activityMessages)
 * @param {string} userId – current user's id (to skip own messages)
 * @param {string} language – target language code (e.g. "de", "it")
 * @returns {Function} cancel – call to abort the pre-translation run
 */
export function preTranslateChats(chats, userId, language) {
  const generation = ++preTranslateGeneration;

  (async () => {
    for (const chat of chats) {
      if (generation !== preTranslateGeneration) return;

      try {
        // Skip entries with no group reference – these are stale/broken
        // chat list items whose underlying group is gone, so a /getChat
        // call always responds with {status:false}.
        if (!chat.group) continue;

        const chatId = chat.group?.isOneToOne
          ? (chat.id || chat.mainId)
          : (chat.mainId || chat.id);
        if (!chatId) continue;
        if (failedPreTranslateChatIds.has(chatId)) continue;

        let apiUrl = `/group/${chatId}/getChat?page=1&limit=30&translationLang=${language}`;
        if (chat.group?.isOneToOne && userId) {
          apiUrl += `&userId=${userId}`;
        } else if (!chat.group?.isOneToOne && chat.building) {
          apiUrl += `&building=${chat.building}`;
        }

        const { data } = await callAPI(apiUrl, {}, 'GET');
        if (data?.status === false) {
          failedPreTranslateChatIds.add(chatId);
          persistFailedPreTranslateChatIds(failedPreTranslateChatIds);
          continue;
        }
        if (!data?.chatHistory) continue;

        for (const msg of data.chatHistory) {
          if (generation !== preTranslateGeneration) return;
          if (msg.senderId === userId) continue;
          if (!msg.message && !(msg.messageType === 'audio' && msg.transcribedAudio)) continue;
          if (msg.message && msg.message.startsWith('U2FsdGVkX1')) continue;
          if (msg.messageType === 'poll') continue;
          if (msg.message && isUntranslatableText(msg.message)) continue;
          if (msg.translatedMessage) {
            setCachedTranslation(
              msg.message,
              language,
              preserveOriginalFormatting(msg.translatedMessage, msg.message)
            );
          } else if (msg.message) {
            await translateMessageContent(msg.message, language);
          }

          if (msg.inReplyTo?.message && !msg.inReplyTo.message.startsWith('U2FsdGVkX1')) {
            await translateMessageContent(msg.inReplyTo.message, language);
          }

          if (msg.translatedAudio && msg.transcribedAudio) {
            setCachedTranslation(
              msg.transcribedAudio,
              language,
              preserveOriginalFormatting(msg.translatedAudio, msg.transcribedAudio)
            );
          } else if (msg.transcribedAudio) {
            await getTranslateText(msg.transcribedAudio, language);
          }
        }
      } catch (_) {
        // skip failed chats silently
      }
    }
  })();

  return function cancel() {
    if (generation === preTranslateGeneration) {
      preTranslateGeneration++;
    }
  };
}

export function cancelPreTranslation() {
  preTranslateGeneration++;
}

// ---------------------------------------------------------------------------
// Article translation – translates title and description for article arrays,
